import { startSubmit, stopSubmit } from 'redux-form'
import { browserHistory } from 'react-router'

import {
  createClient,
  currentUserSelector,
  hydrateAuthentication
} from 'auth'
import { openModal } from 'modal'
import { discountCodesSelector } from 'order-summary'
import { login } from 'registration/actions'
import {
  createStripeChargeToken,
  billingAddressSelector,
  handlePaymentError
} from 'stripe'
import { invoiceRecipientSelector } from 'invoice-request-form'

import ConfirmModal from './ConfirmModal'
import { INDIVIDUAL_RESERVATION_FORM_NAME } from '../constants'
import { invoiceRequestedSelector, getIndividualReservationOrderSummary, registrationFormSelector, userRegistrationErrorsSelector } from './selectors'
import {
  CLEAR_USER_REGISTRATION_ERROR,
  SET_USER_REGISTRATION_ERROR,
  SET_USER_REGISTRATION_SELECTED_INSTITUTION,
  LOAD_INTAKE_QUESTIONS_SUCCESS,
  LOAD_INTAKE_QUESTIONS_ERROR
} from './types'
import { BadRequestError } from 'api'
import { isEmpty } from 'lodash'

// The institution search results (`GET /v2/institutions`) don't include
// `school_or_college_options` - only fetching a single institution does. So once an
// institution is selected, re-fetch it directly to find out whether it has a
// school/college list the user needs to pick from.
export const selectInstitution = (institution) => (dispatch, getState) => {
  dispatch({ type: SET_USER_REGISTRATION_SELECTED_INSTITUTION, institution })

  if (!institution) return

  const client = createClient(getState())
  return client.get(`/v2/institutions/${institution.id}`)
    .then(response => response.json())
    .then(fullInstitution => {
      dispatch({
        type: SET_USER_REGISTRATION_SELECTED_INSTITUTION,
        institution: { ...institution, ...fullInstitution }
      })
    })
    .catch(() => {
      // Non-fatal: the school/college field just won't be offered, and the
      // backend will still reject the submission if it turns out to be required.
    })
}

export const loadIntakeQuestions = (sessionSaleId) => (dispatch, getState) => {
  const client = createClient(getState())

  return client.get(`/v2/session_sales/${sessionSaleId}/intake_questions`)
    .then(response => response.json())
    .then(data => {
      dispatch({
        type: LOAD_INTAKE_QUESTIONS_SUCCESS,
        questions: data.data
      })
    })
    .catch(err => {
      dispatch({
        type: LOAD_INTAKE_QUESTIONS_ERROR,
        error: err
      })
    })
}

export const reserve = (props) => (dispatch, getState) => {
  const stateWhenSubmitting = getState()
  const currentUser = currentUserSelector(stateWhenSubmitting)
  const registrationInfo = registrationFormSelector(INDIVIDUAL_RESERVATION_FORM_NAME)(stateWhenSubmitting) // for new user if creating new user during session sale sign up
  const formAddress = billingAddressSelector(INDIVIDUAL_RESERVATION_FORM_NAME)(stateWhenSubmitting)
  const discountCodes = discountCodesSelector(INDIVIDUAL_RESERVATION_FORM_NAME)(stateWhenSubmitting)
  const orderSummary = getIndividualReservationOrderSummary(stateWhenSubmitting, props)
  const invoiceRequested = invoiceRequestedSelector(stateWhenSubmitting)
  const invoiceRecipient = invoiceRecipientSelector(INDIVIDUAL_RESERVATION_FORM_NAME)(stateWhenSubmitting)

  // Collect intake question answers from form
  const formValues = stateWhenSubmitting.form[INDIVIDUAL_RESERVATION_FORM_NAME]?.values || {}
  const intakeAnswers = []

  // Get intake questions from Redux state
  const intakeQuestions = stateWhenSubmitting.individualReservations?.intakeQuestions || []

  intakeQuestions.forEach(question => {
    const answerKey = `intake_answer_${question.id}`
    const answerValue = formValues[answerKey]
    if (answerValue !== undefined && answerValue !== null && answerValue !== '') {
      intakeAnswers.push({
        intake_question_id: question.id,
        answer_value: answerValue
      })
    }
  })

  const errors = userRegistrationErrorsSelector(INDIVIDUAL_RESERVATION_FORM_NAME)(stateWhenSubmitting)
  if (!isEmpty(errors)) {
    document.getElementById('session-sale-new-user-registration-form')?.scrollIntoView({
      behavior: 'smooth'
    })
    return
  }

  dispatch({ type: CLEAR_USER_REGISTRATION_ERROR })
  dispatch(startSubmit(INDIVIDUAL_RESERVATION_FORM_NAME))

  let registerPromise = Promise.resolve()
  let newMembershipCreated = false
  if (!currentUser &&
    registrationInfo.registration_first_name &&
    registrationInfo.registration_last_name &&
    registrationInfo.registration_email &&
    registrationInfo.registration_password
  ) {
    registerPromise = createClient(stateWhenSubmitting).post('/v2/users', {
      body: {
        first_name: registrationInfo.registration_first_name,
        last_name: registrationInfo.registration_last_name,
        email: registrationInfo.registration_email,
        password: registrationInfo.registration_password,
        institution_id: stateWhenSubmitting?.individualReservations?.selectedInstitution?.id,
        school_or_college_id: formValues.school_or_college_id,
        school_or_college_not_listed: formValues.school_or_college_not_listed,
        session_sale_id: props?.sessionSale?.id
      }
    })
      .then(() => {
        newMembershipCreated = true
        return dispatch(login(registrationInfo.registration_email, registrationInfo.registration_password))
      })
  }

  return registerPromise.then(() => {
    const currentUser = currentUserSelector(getState())
    const client = createClient(getState())

    const updatePromises = [
      Promise.resolve(
        orderSummary.total > 0 && !invoiceRequested
          ? createStripeChargeToken(props.stripeElements.cardNumber)
          : null
      )
        .then(chargeToken => {
          const body = {
            type: 'user',
            newMembershipCreated
          }

          if (chargeToken) {
            body.stripe_charge_token = chargeToken.id
            body.invoice_recipient = {
              address_line_1: formAddress.address_line_1,
              address_line_2: formAddress.address_line_2,
              // we're not capturing name or email in this form so get those from the user's account
              billing_name: currentUser.name,
              billing_email: currentUser.email,
              city: formAddress.city,
              country: formAddress.country,
              institution: formAddress.institution,
              postal_code: formAddress.postal_code,
              state: formAddress.state
            }
          }

          if (invoiceRequested && orderSummary.total > 0) {
            body.invoice_recipient = invoiceRecipient
          }

          if (discountCodes) {
            body.discount_codes = discountCodes
          }

          return client.post(`/v2/session_sales/${props.sessionSale.id}/reservations`, { body })
        })
    ]
    return Promise.all(updatePromises)
      .then(([createReservationResponse]) => {
        dispatch(stopSubmit(INDIVIDUAL_RESERVATION_FORM_NAME))
        return createReservationResponse.json()
          .then(reservation => {
            // Submit intake answers if any
            const submitIntakeAnswers = intakeAnswers.length > 0
              ? client.post(`/v2/session_reservations/${reservation.id}/intake_answers`, {
                  body: { answers: intakeAnswers }
                })
              : Promise.resolve()

            return submitIntakeAnswers
              .then(() => client.get(`/v2/orders/by-order-item/${reservation.order_item_id}`))
              .then(res => res.json())
              .then(order => {
                dispatch(hydrateAuthentication())
                browserHistory.push(`/sessions/${props.sessionSale.slug}`)
                dispatch(openModal(ConfirmModal, {
                  sessionSale: props.sessionSale,
                  order,
                  invoiceRequested
                }))
              })
          })
      })
      .catch(err => {
        dispatch(handlePaymentError(err, INDIVIDUAL_RESERVATION_FORM_NAME))
      })
  }).catch(err => {
    dispatch(stopSubmit(INDIVIDUAL_RESERVATION_FORM_NAME))
    if (err instanceof BadRequestError) {
      return err.response.json()
        .then(body => {
          if (body?.errors) {
            dispatch({
              type: SET_USER_REGISTRATION_ERROR,
              errors: body.errors
            })
            document.getElementById('session-sale-new-user-registration-form')?.scrollIntoView({
              behavior: 'smooth'
            })
          }
        })
    } else {
      dispatch(handlePaymentError(err, INDIVIDUAL_RESERVATION_FORM_NAME))
    }
  })
}
